import { Order } from '../../services/exports/Interfaces';
import classnames from 'classnames';
import useAppMethods from '../../hooks/utility/useAppMethods';
import { useTranslation } from 'react-i18next';
import Tooltip from '../Tooltip';
import InfoIcon from '../../assets/logo/InfoIcon';
import useTheme from '../../hooks/ui/useTheme';
import { ReactElement } from 'react';
import CustomLoadingIndicator from '../loading/CustomLoadingIndicator';

interface Props {
  order: Order;
  loading?: boolean;
  className?: string;
}

export default function OrderPaymentBreakdown({ order, loading, className }: Props) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Order:OrderPaymentBreakdown' });

  const theme = useTheme();
  const { formatCurrency } = useAppMethods();

  const Row = ({
    name,
    amount,
    content,
    className,
  }: {
    name: any;
    amount?: number;
    content?: ReactElement;
    className?: string;
  }) => (
    <div className={classnames('flex justify-between text-sm gap-lg', className)}>
      <span className="flex items-center">{name}</span>
      <span>{content ?? formatCurrency(amount)}</span>
    </div>
  );

  return (
    <div className={className}>
      <Row name={t('subtotal')} amount={order.sub_total} />
      {order.tip > 0 && <Row name={t('tip')} amount={order.tip} className="mt-xs" />}
      {order.method_fee > 0 && <Row name={t('method_fee')} amount={order.method_fee} className="mt-xs" />}
      {order.service_fee > 0 && <Row name={t('service_fee')} amount={order.service_fee} className="mt-xs" />}
      {order.payment_method_upcharge_fee > 0 && (
        <Row
          name={
            <>
              {t('payment_method_upcharge_fee.title')}{' '}
              <Tooltip title={t(`payment_method_upcharge_fee.description`)} className="my-auto ml-2xs">
                <InfoIcon color={theme.color.gray['300']} className="w-[18px] h-[18px]" />
              </Tooltip>
            </>
          }
          amount={order.payment_method_upcharge_fee}
          className="mt-xs relative"
        />
      )}
      {order.additional_tax > 0 && <Row name={t('tax')} amount={order.additional_tax} className="mt-xs" />}
      {order.total_discount > 0 && <Row name={t('discount')} amount={-order.total_discount} className="mt-xs" />}
      <div className="flex justify-between text-sm gap-lg !text-base font-medium mt-xs">
        <span className="flex items-center">{t('total')}</span>
        <span>
          <span className={classnames({ hidden: loading })}>{formatCurrency(order.total)}</span>
          <CustomLoadingIndicator className={classnames('w-12', { hidden: !loading })} />
        </span>
      </div>
    </div>
  );
}
